import type { ExpressionSpecification, SymbolLayerSpecification } from 'mapbox-gl';
import { FONT_SIZE_100 } from '@alltrails/denali/tokens';
import { defaultPinLayerLayoutProps, defaultSymbolLayerLayoutProps } from './layers';
import {
  isMultiTrailExpression,
  isTrailClosedExpression,
  isTrailCollapsedExpression,
  isTrailCompletedExpression,
  isTrailSavedExpression,
  multiTrailPinActiveImageNameBase,
  multiTrailPinClosedActiveImageNameBase,
  multiTrailPinClosedImageNameBase,
  multiTrailPinCompletedActiveImageNameBase,
  multiTrailPinCompletedImageNameBase,
  multiTrailPinImageNameBase,
  multiTrailPinSavedActiveImageNameBase,
  multiTrailPinSavedImageNameBase,
  trailPinActiveImageName,
  trailPinClosedActiveImageName,
  trailPinClosedImageName,
  trailPinCollapsedClosedImageName,
  trailPinCollapsedCompletedImageName,
  trailPinCollapsedImageName,
  trailPinCollapsedSavedImageName,
  trailPinCollapsedViewedImageName,
  trailPinCompletedActiveImageName,
  trailPinCompletedImageName,
  trailPinImageName,
  trailPinSavedActiveImageName,
  trailPinSavedImageName
} from './trailPinConstants';

export function buildCommonLayoutProps(
  isHoveredExpression: ExpressionSpecification,
  isSelectedExpression: ExpressionSpecification
): SymbolLayerSpecification['layout'] {
  return {
    ...defaultSymbolLayerLayoutProps,
    ...defaultPinLayerLayoutProps,
    'symbol-sort-key': ['case', ['any', isHoveredExpression, isSelectedExpression], 1, 0],
    'icon-allow-overlap': true
  };
}

export function buildDynamicTrailPinProps(
  isHoveredExpression: ExpressionSpecification,
  isSelectedExpression: ExpressionSpecification
): SymbolLayerSpecification['layout'] {
  return {
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-text-fit': 'both',
    'text-field': ['get', 'lengthDisplay'],
    'text-allow-overlap': true,
    'symbol-sort-key': ['case', ['any', isHoveredExpression, isSelectedExpression], 100, ['get', 'popularity']],
    'text-font': ['AllTrails Aeonik Medium', 'Manrope Bold'],
    'text-size': FONT_SIZE_100,
    'text-line-height': 1
  };
}

export function buildRegularTrailIconImage(
  isHoveredExpression: ExpressionSpecification,
  isSelectedExpression: ExpressionSpecification,
  isActiveTrailExpression: ExpressionSpecification
): ExpressionSpecification {
  return [
    'case',
    // Selected, closed multi-trail
    ['all', isSelectedExpression, isMultiTrailExpression, isTrailClosedExpression],
    ['concat', multiTrailPinClosedActiveImageNameBase, ['get', 'trailCount']],
    // Hovered, closed multi-trail
    ['all', isHoveredExpression, isMultiTrailExpression, isTrailClosedExpression],
    ['concat', multiTrailPinClosedActiveImageNameBase, ['get', 'trailCount']],
    // Active, closed multi-trail
    ['all', isActiveTrailExpression, isMultiTrailExpression, isTrailClosedExpression],
    ['concat', multiTrailPinClosedActiveImageNameBase, ['get', 'trailCount']],
    // Default, closed multi-trail
    ['all', isMultiTrailExpression, isTrailClosedExpression],
    ['concat', multiTrailPinClosedImageNameBase, ['get', 'trailCount']],

    // Saved multi-trails
    ['all', isSelectedExpression, isMultiTrailExpression, isTrailSavedExpression],
    ['concat', multiTrailPinSavedActiveImageNameBase, ['get', 'trailCount']],
    ['all', isHoveredExpression, isMultiTrailExpression, isTrailSavedExpression],
    ['concat', multiTrailPinSavedActiveImageNameBase, ['get', 'trailCount']],
    ['all', isActiveTrailExpression, isMultiTrailExpression, isTrailSavedExpression],
    ['concat', multiTrailPinSavedActiveImageNameBase, ['get', 'trailCount']],
    ['all', isMultiTrailExpression, isTrailSavedExpression],
    ['concat', multiTrailPinSavedImageNameBase, ['get', 'trailCount']],

    // Completed multi-trails
    ['all', isSelectedExpression, isMultiTrailExpression, isTrailCompletedExpression],
    ['concat', multiTrailPinCompletedActiveImageNameBase, ['get', 'trailCount']],
    ['all', isHoveredExpression, isMultiTrailExpression, isTrailCompletedExpression],
    ['concat', multiTrailPinCompletedActiveImageNameBase, ['get', 'trailCount']],
    ['all', isActiveTrailExpression, isMultiTrailExpression, isTrailCompletedExpression],
    ['concat', multiTrailPinCompletedActiveImageNameBase, ['get', 'trailCount']],
    ['all', isMultiTrailExpression, isTrailCompletedExpression],
    ['concat', multiTrailPinCompletedImageNameBase, ['get', 'trailCount']],

    // Regular multi-trails
    ['all', isSelectedExpression, isMultiTrailExpression],
    ['concat', multiTrailPinActiveImageNameBase, ['get', 'trailCount']],
    ['all', isHoveredExpression, isMultiTrailExpression],
    ['concat', multiTrailPinActiveImageNameBase, ['get', 'trailCount']],
    ['all', isActiveTrailExpression, isMultiTrailExpression],
    ['concat', multiTrailPinActiveImageNameBase, ['get', 'trailCount']],
    isMultiTrailExpression,
    ['concat', multiTrailPinImageNameBase, ['get', 'trailCount']],

    // Closed single trails
    ['all', isSelectedExpression, isTrailClosedExpression],
    trailPinClosedActiveImageName,
    ['all', isHoveredExpression, isTrailClosedExpression],
    trailPinClosedActiveImageName,
    ['all', isActiveTrailExpression, isTrailClosedExpression],
    trailPinClosedActiveImageName,
    isTrailClosedExpression,
    trailPinClosedImageName,

    // Saved single trails
    ['all', isSelectedExpression, isTrailSavedExpression],
    trailPinSavedActiveImageName,
    ['all', isHoveredExpression, isTrailSavedExpression],
    trailPinSavedActiveImageName,
    ['all', isActiveTrailExpression, isTrailSavedExpression],
    trailPinSavedActiveImageName,
    isTrailSavedExpression,
    trailPinSavedImageName,

    // Completed single trails
    ['all', isSelectedExpression, isTrailCompletedExpression],
    trailPinCompletedActiveImageName,
    ['all', isHoveredExpression, isTrailCompletedExpression],
    trailPinCompletedActiveImageName,
    ['all', isActiveTrailExpression, isTrailCompletedExpression],
    trailPinCompletedActiveImageName,
    isTrailCompletedExpression,
    trailPinCompletedImageName,

    // Default single trails
    isSelectedExpression,
    trailPinActiveImageName,
    isHoveredExpression,
    trailPinActiveImageName,
    isActiveTrailExpression,
    trailPinActiveImageName,
    trailPinImageName
  ];
}

export function buildCollapsedTrailIconImage(
  isHoveredExpression: ExpressionSpecification,
  isSelectedExpression: ExpressionSpecification,
  isActiveTrailExpression: ExpressionSpecification
): ExpressionSpecification {
  // Icon image expression for collapsed trail pins (old pins experiment)
  return [
    'case',

    // Closed collapsed trails
    ['all', isSelectedExpression, isTrailClosedExpression, isTrailCollapsedExpression],
    trailPinClosedActiveImageName,
    ['all', isHoveredExpression, isTrailClosedExpression, isTrailCollapsedExpression],
    trailPinClosedActiveImageName,
    ['all', isActiveTrailExpression, isTrailClosedExpression, isTrailCollapsedExpression],
    trailPinClosedActiveImageName,
    ['all', isTrailClosedExpression, isTrailCollapsedExpression],
    trailPinCollapsedClosedImageName,

    // Saved collapsed trails
    ['all', isSelectedExpression, isTrailSavedExpression, isTrailCollapsedExpression],
    trailPinSavedActiveImageName,
    ['all', isHoveredExpression, isTrailSavedExpression, isTrailCollapsedExpression],
    trailPinSavedActiveImageName,
    ['all', isActiveTrailExpression, isTrailSavedExpression, isTrailCollapsedExpression],
    trailPinSavedActiveImageName,
    ['all', isTrailSavedExpression, isTrailCollapsedExpression],
    trailPinCollapsedSavedImageName,

    // Completed collapsed trails
    ['all', isSelectedExpression, isTrailCompletedExpression, isTrailCollapsedExpression],
    trailPinCompletedActiveImageName,
    ['all', isHoveredExpression, isTrailCompletedExpression, isTrailCollapsedExpression],
    trailPinCompletedActiveImageName,
    ['all', isActiveTrailExpression, isTrailCompletedExpression, isTrailCollapsedExpression],
    trailPinCompletedActiveImageName,
    ['all', isTrailCompletedExpression, isTrailCollapsedExpression],
    trailPinCollapsedCompletedImageName,

    // When hovered/selected/active, show the regular active pin
    ['any', isSelectedExpression, isHoveredExpression, isActiveTrailExpression],
    trailPinActiveImageName,
    // Default collapsed
    trailPinCollapsedImageName
  ];
}

export function buildNewRegularTrailIconImage(
  isHoveredExpression: ExpressionSpecification,
  isSelectedExpression: ExpressionSpecification,
  isViewedExpression: ExpressionSpecification,
  isActiveTrailExpression: ExpressionSpecification
): ExpressionSpecification {
  const isActiveOrHovered = ['any', isHoveredExpression, isSelectedExpression, isActiveTrailExpression];

  return [
    'case',
    // Closed single trails
    ['all', isActiveOrHovered, isTrailClosedExpression],
    'trail-length-pin-stretchable-active-closed',
    ['all', isViewedExpression, isTrailClosedExpression],
    'trail-length-pin-stretchable-viewed-closed',
    isTrailClosedExpression,
    'trail-length-pin-stretchable-default-closed',

    // Saved single trails
    ['all', isActiveOrHovered, isTrailSavedExpression],
    'trail-length-pin-stretchable-active-saved',
    ['all', isViewedExpression, isTrailSavedExpression],
    'trail-length-pin-stretchable-viewed-saved',
    isTrailSavedExpression,
    'trail-length-pin-stretchable-default-saved',

    // Completed single trails
    ['all', isActiveOrHovered, isTrailCompletedExpression],
    'trail-length-pin-stretchable-active-completed',
    ['all', isViewedExpression, isTrailCompletedExpression],
    'trail-length-pin-stretchable-viewed-completed',
    isTrailCompletedExpression,
    'trail-length-pin-stretchable-default-completed',

    isActiveOrHovered,
    'trail-length-pin-stretchable-active-trail',
    isViewedExpression,
    'trail-length-pin-stretchable-viewed-trail',
    // Default fallback
    'trail-length-pin-stretchable-default-trail'
  ];
}

export function buildNewCollapsedTrailIconImage(
  isHoveredExpression: ExpressionSpecification,
  isSelectedExpression: ExpressionSpecification,
  isViewedExpression: ExpressionSpecification,
  isActiveTrailExpression: ExpressionSpecification
): ExpressionSpecification {
  const isActiveOrHovered = ['any', isHoveredExpression, isSelectedExpression, isActiveTrailExpression];

  // Icon image expression for collapsed trail pins (new pins experiment)
  // When hovered/selected/active, show the full stretchable pill-shaped pin
  return [
    'case',

    // Closed collapsed trails
    ['all', isActiveOrHovered, isTrailClosedExpression, isTrailCollapsedExpression],
    'trail-length-pin-stretchable-active-closed',
    ['all', isViewedExpression, isTrailClosedExpression, isTrailCollapsedExpression],
    trailPinCollapsedClosedImageName,
    ['all', isTrailClosedExpression, isTrailCollapsedExpression],
    trailPinCollapsedClosedImageName,

    // Saved collapsed trails
    ['all', isActiveOrHovered, isTrailSavedExpression, isTrailCollapsedExpression],
    'trail-length-pin-stretchable-active-saved',
    ['all', isViewedExpression, isTrailSavedExpression, isTrailCollapsedExpression],
    trailPinCollapsedSavedImageName,
    ['all', isTrailSavedExpression, isTrailCollapsedExpression],
    trailPinCollapsedSavedImageName,

    // Completed collapsed trails
    ['all', isActiveOrHovered, isTrailCompletedExpression, isTrailCollapsedExpression],
    'trail-length-pin-stretchable-active-completed',
    ['all', isViewedExpression, isTrailCompletedExpression, isTrailCollapsedExpression],
    trailPinCollapsedCompletedImageName,
    ['all', isTrailCompletedExpression, isTrailCollapsedExpression],
    trailPinCollapsedCompletedImageName,

    // When hovered/selected/active, show the regular active pin
    ['any', isActiveOrHovered],
    // Use the stretchable pill-shaped pin when active
    'trail-length-pin-stretchable-active-trail',
    isViewedExpression,
    trailPinCollapsedViewedImageName,

    // Default collapsed
    trailPinCollapsedImageName
  ];
}

export function buildCollapsedPinFilters(
  isHoveredExpression: ExpressionSpecification,
  isSelectedExpression: ExpressionSpecification,
  isActiveTrailExpression: ExpressionSpecification
): {
  isCollapsedInactiveFilter: ExpressionSpecification;
  isCollapsedActiveFilter: ExpressionSpecification;
} {
  // For new pins: inactive collapsed (no icon-offset jump when expanding); active collapsed on separate layer with no icon-offset
  const isCollapsedInactiveFilter = [
    'all',
    ['!', ['has', 'point_count']],
    isTrailCollapsedExpression,
    ['!', ['any', isHoveredExpression, isSelectedExpression, isActiveTrailExpression]]
  ] as ExpressionSpecification;
  const isCollapsedActiveFilter = [
    'all',
    ['!', ['has', 'point_count']],
    isTrailCollapsedExpression,
    ['any', isHoveredExpression, isSelectedExpression, isActiveTrailExpression]
  ] as ExpressionSpecification;

  return {
    isCollapsedInactiveFilter,
    isCollapsedActiveFilter
  };
}
